import React, { useState, useCallback, useMemo } from 'react'

import { colors } from 'Constants'

import { toast } from 'react-toastify'
import { useInput, debounce, camelToReadable, constantToReadable } from 'Helpers'
import { toastWithErrorMessage } from 'Helpers/toast'

import Input from 'Components/shared/Input'
import { Button } from 'Components/shared/Button'
import { Flex } from 'lese'

import { createModal, closeModal } from 'Components/Modal'
import { ConfirmationModalStrict } from 'Components/Modal/ConfirmationModal'
import UsersService from 'Services/users'
import { SelectUsingButton } from 'Components/shared/Select'
import { PERMISSIONS } from 'Root/../../shared/constants'

export default ({ user, setUser }) => {
  const updateUser = useCallback(
    key =>
      debounce(
        async prop =>
          UsersService.update({
            userId: user.id,
            [key]: prop
          })
            .then(() => toast.success(`Successfully updated ${camelToReadable(key)}`))
            .catch(toastWithErrorMessage(`Failed to update ${camelToReadable(key)}`)),
        1000
      ),
    [user]
  )

  const updateUserUsername = useMemo(() => updateUser('username'), [updateUser])
  const updateUserFirstName = useMemo(() => updateUser('firstName'), [updateUser])
  const updateUserLastName = useMemo(() => updateUser('lastName'), [updateUser])

  const updateUserPermissionLevel = useCallback(
    async permissionLevel =>
      UsersService.update({
        userId: user.id,
        permissionLevel
      }).catch(toastWithErrorMessage(`Failed to update permission level`)),
    [user]
  )

  const usernameHook = useInput(user.username, updateUserUsername)
  const firstNameHook = useInput(user.firstName, updateUserFirstName)
  const lastNameHook = useInput(user.lastName, updateUserLastName)

  const [permissionLevel, setPermissionLevel] = useState(user.permissionLevel)
  const permissionLevelOptions = useMemo(
    () =>
      Object.entries(PERMISSIONS).map(val => ({
        text: constantToReadable(val[0]),
        value: val[1],
        color: colors.blue
      })),
    []
  )

  return (
    <Flex column separation="24px" xAlign="center">
      <Input placeholder="Username" {...usernameHook.bind} tabIndex={0} />
      <Input placeholder="First Name" {...firstNameHook.bind} tabIndex={0} />
      <Input placeholder="Last Name" {...lastNameHook.bind} tabIndex={0} />
      <SelectUsingButton
        selection={permissionLevel}
        options={permissionLevelOptions}
        onChange={({ newIndex }) => {
          updateUserPermissionLevel(permissionLevelOptions[newIndex].value).then(() =>
            setPermissionLevel(permissionLevelOptions[newIndex].value)
          )
        }}
      />
      <Flex separation="24px">
        <Button
          color={colors.redMuted}
          onClick={() =>
            createModal(
              <ConfirmationModalStrict
                requiredText={user.username}
                description={
                  <>
                    Are you sure you want to {user?.archived ? 'unarchive' : 'archive'} this user?
                    <br />
                    This process is reversable.
                  </>
                }
                onConfirm={() => {
                  UsersService.update({
                    userId: user.id,
                    archived: !user.archived
                  })
                    .then(() => {
                      closeModal()
                      setUser({ ...user, archived: !user.archived })
                    })
                    .catch(
                      toastWithErrorMessage(
                        `Failed to ${user?.archived ? 'unarchive' : 'archive'} virtual office`
                      )
                    )
                }}
              />
            )
          }
        >
          {user?.archived ? 'Unarchive' : 'Archive'} User
        </Button>
        <Button
          color={colors.redMuted}
          disabled
          onClick={() =>
            createModal(
              <ConfirmationModalStrict
                requiredText={user.username}
                description={
                  <>
                    Are you sure you want to delete this user?
                    <br />
                    This process is not reversable.
                  </>
                }
                onConfirm={closeModal}
                onCancel={closeModal}
              />
            )
          }
        >
          Delete User
        </Button>
      </Flex>
    </Flex>
  )
}
